
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import { sharedState } from '../../../framework/state';
import { getEnumerationOptions, getEnumOptions } from '../../../framework/service/options';

import CustomerServiceInfo from './CustomerServiceInfo.vue';
import RoadServiceInfo from './RoadServiceInfo.vue';
import CarPolicyInfo from './CarPolicyInfo.vue';

import { ServiceInfoType } from '../../../../common/application/model/enums/ServiceInfoType';
import { VendorContractPublic } from '../../../../common/application/model/VendorContractPublic';
import { VendorContractType } from '../../../../common/application/model/enums/VendorContractType';
import { ApplicationResource } from '../../../../common/application/enumeration/ApplicationResource';

import { getSmartFleetDefaultVendorLocations, getUserServices } from '../../client/vendor_contract_client';
import { getGoogleMapsLinkForContract } from '../../service/google_maps_helper_service';

@Component({
    components: { CustomerServiceInfo, RoadServiceInfo, CarPolicyInfo },
})
export default class ServiceInfo extends Vue {
    // Constants
    readonly resourceType = ApplicationResource.VEHICLE;

    @Prop(String) readonly registrationNumber!: string;
    @Prop(String) readonly type!: ServiceInfoType;

    // State
    shared = sharedState;
    local = {
        loading: true,
        vehicleRegistrationNumber: '',
        publicVendorContracts: [] as VendorContractPublic[],
        relevantVendorContracts: [] as VendorContractPublic[],
        vendorContractTypeOptions: [] as {
            id: string | undefined;
            label: string;
        }[],
    };

    // Functions
    async mounted() {
        this.local.loading = true;

        this.local.vendorContractTypeOptions = getEnumerationOptions(
            'VendorContractType',
            Object.keys(VendorContractType),
            this,
        );
        await this.loadAsyncData();

        this.local.loading = false;
    }

    private async loadAsyncData(): Promise<void> {
        this.local.publicVendorContracts = await getUserServices(this.registrationNumber);

        this.filterRelevantContracts();

        if (this.local.relevantVendorContracts.length === 0) {
            const defaultContracts = await getSmartFleetDefaultVendorLocations(this.registrationNumber);

            defaultContracts.forEach((contract) => this.local.publicVendorContracts.push(contract));
            this.filterRelevantContracts();
        }

        return;
    }

    @Watch('local.vehicleRegistrationNumber')
    onRegistrationNumberChange(newValue: string) {
        if (newValue.length === 3) {
            this.local.vehicleRegistrationNumber += '-';
        }
    }

    back() {
        this.$router.go(-1);
    }

    filterRelevantContracts(): void {
        this.local.publicVendorContracts.forEach((contract) => {
            switch (contract.vendorContractType) {
                case VendorContractType.MAINTENANCE:
                case VendorContractType.IMPORTER_CARE_SUPPORT:
                    if (this.type === ServiceInfoType.BOOK_SERVICE) {
                        this.local.relevantVendorContracts.push(contract);
                    }
                    break;
                case VendorContractType.SUBSTITUTE_VEHICLE:
                    if (this.type === ServiceInfoType.RENTAL_CAR) {
                        this.local.relevantVendorContracts.push(contract);
                    }
                    break;
                case VendorContractType.TIRE_RENEWAL:
                case VendorContractType.TIRE_STORAGE:
                    if (this.type === ServiceInfoType.TIRES) {
                        this.local.relevantVendorContracts.push(contract);
                    }
                    break;
                case VendorContractType.WASHING:
                    if (this.type === ServiceInfoType.WASHES) {
                        this.local.relevantVendorContracts.push(contract);
                    }
                    break;
                case VendorContractType.INSURANCE:
                    if (this.type === ServiceInfoType.INSURANCE) {
                        this.local.relevantVendorContracts.push(contract);
                    }
                    break;
                case VendorContractType.FUEL_CARD:
                case VendorContractType.ELECTRICAL_CHARGING:
                    if (this.type === ServiceInfoType.FUEL) {
                        this.local.relevantVendorContracts.push(contract);
                    }
                    break;
                case VendorContractType.INSPECTION:
                    if (this.type === ServiceInfoType.VEHICLE_INSPECTION) {
                        this.local.relevantVendorContracts.push(contract);
                    }
                    break;
                default:
                    if (
                        this.type === ServiceInfoType.CUSTOMER_SERVICE &&
                        contract.vendorContractType === VendorContractType.UNSPECIFIED
                    ) {
                        this.local.relevantVendorContracts.push(contract);
                    }
                    break;
            }
        });
    }

    optionLabel(vendorContract: VendorContractPublic): string {
        const enumOptions = getEnumOptions('VendorContractType', Object.keys(VendorContractType));
        const enumOption = enumOptions.find((enumOption) => enumOption.id === vendorContract.vendorContractType);

        if (!enumOption) {
            return vendorContract.vendorContractType.toString();
        }

        return this.$t(enumOption.labelKey).toString();
    }

    getGoogleMapsLink(contract: VendorContractPublic): string {
        return getGoogleMapsLinkForContract(contract);
    }

    goToHomePage(contract?: VendorContractPublic): void {
        if (!contract) {
            window.open(`https://${this.$t('label.smartFleetUrl')}/`, '_blank');
        } else {
            window.open(`${contract.vendorLocationUrl}`, '_blank');
        }
    }

    get isCustomerServiceType(): boolean {
        return this.type === ServiceInfoType.CUSTOMER_SERVICE;
    }

    get isRoadServiceType(): boolean {
        return this.type === ServiceInfoType.ROAD_SERVICE;
    }

    get isCarPolicyType(): boolean {
        return this.type === ServiceInfoType.CAR_POLICY;
    }

    get isReturnInspectionType(): boolean {
        return this.type === ServiceInfoType.CAR_CHANGE;
    }
}
